import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  VisuallyHidden,
} from "@mantine/core";
import { useState } from "react";
import { LoadingState } from "@how/ui";
import type { ShopProduct } from "../shop/shop-queries";
import {
  formatPrice,
  type MyRegistration,
  type UpcomingOccurrence,
  useAddAddOns,
  useEventAddOns,
} from "./event-queries";

/**
 * Add-ons for one registration (ADR 0047 step 3): what the event offers, picked with a quantity each. A place still to
 * be paid is paid together with them; otherwise they are paid now. Handed over at the event.
 */
export function AddOnsModal({
  occurrence,
  registration,
  onClose,
}: {
  readonly occurrence: UpcomingOccurrence;
  readonly registration: MyRegistration;
  readonly onClose: () => void;
}) {
  const offered = useEventAddOns(occurrence.event.id);
  const add = useAddAddOns();
  const [picked, setPicked] = useState<Record<string, number>>({});
  // The spoken total starts empty and is filled on the first choice, so opening the picker announces nothing (#277).
  const [chosen, setChosen] = useState(false);
  const payWithPlace = registration.paymentStatus === "pending";
  const products = offered.data ?? [];
  const prices = new Map(
    products.flatMap((p) => p.variants.map((v) => [v.id, v.priceCents] as const)),
  );
  const lines = Object.entries(picked)
    .filter(([, quantity]) => quantity > 0)
    .map(([variantId, quantity]) => ({ variantId, quantity }));
  const total = lines.reduce((sum, l) => sum + (prices.get(l.variantId) ?? 0) * l.quantity, 0);
  const currency = products[0]?.currency ?? occurrence.event.currency;
  const who = registration.isMe ? "you" : registration.name;

  return (
    <Modal opened onClose={onClose} title={`Add-ons for ${occurrence.event.title}`} size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          For {who}. Handed over at the event.
          {payWithPlace
            ? " They are added to the place you have not paid for yet, and paid together."
            : " You pay for them now."}
        </Text>
        {offered.isPending ? (
          <LoadingState rows={2} />
        ) : offered.isError ? (
          <Alert color="red" variant="light">
            {offered.error.message}
          </Alert>
        ) : products.length === 0 ? (
          <Text size="sm">This event has no add-ons right now.</Text>
        ) : (
          products.map((p) => (
            <AddOnRow
              key={p.id}
              product={p}
              picked={picked}
              onPick={(variantId, quantity) => {
                setChosen(true);
                setPicked((current) => {
                  const others = new Set(p.variants.map((v) => v.id));
                  const next = Object.fromEntries(
                    Object.entries(current).filter(([id]) => !others.has(id)),
                  );
                  return quantity > 0 ? { ...next, [variantId]: quantity } : next;
                });
              }}
            />
          ))
        )}
        {add.isError ? (
          <Alert color="red" variant="light">
            {add.error.message}
          </Alert>
        ) : null}
        <Group justify="space-between">
          <Text size="sm" fw={600} className="how-numeric">
            {lines.length === 0 ? "Nothing chosen" : formatPrice(total, currency)}
          </Text>
          {/* Read out as it changes, so the total is heard, not only seen (#261); empty until something is chosen. */}
          <VisuallyHidden role="status" aria-live="polite">
            {chosen
              ? lines.length === 0
                ? "Nothing chosen"
                : `Total ${formatPrice(total, currency)}`
              : ""}
          </VisuallyHidden>
          <Group gap="xs">
            <Button variant="subtle" onClick={onClose}>
              Not now
            </Button>
            <Button
              loading={add.isPending}
              disabled={lines.length === 0 || total < 50}
              onClick={() => {
                add.mutate(
                  { registrationId: registration.registrationId, lines, payNow: !payWithPlace },
                  { onSuccess: onClose },
                );
              }}
            >
              {payWithPlace ? "Add to my place" : `Pay ${formatPrice(total, currency)}`}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

function AddOnRow({
  product,
  picked,
  onPick,
}: {
  readonly product: ShopProduct;
  readonly picked: Readonly<Record<string, number>>;
  readonly onPick: (variantId: string, quantity: number) => void;
}) {
  const first = product.variants.find((v) => v.available) ?? product.variants[0];
  const current = product.variants.find((v) => (picked[v.id] ?? 0) > 0);
  const [variantId, setVariantId] = useState<string | null>(current?.id ?? first?.id ?? null);
  const chosen = product.variants.find((v) => v.id === variantId) ?? null;
  const quantity = chosen === null ? 0 : (picked[chosen.id] ?? 0);
  const soldOut = product.variants.every((v) => !v.available);
  const max = Math.min(20, typeof chosen?.left === "number" ? chosen.left : 20);

  return (
    <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
      <Stack gap={2} style={{ flex: "1 1 200px" }}>
        <Group gap="xs">
          {/* Under the dialog's own heading, so a screen reader can move product to product (#285). */}
          <Text component="h3" size="sm" fw={500} m={0}>
            {product.name}
          </Text>
          {chosen === null ? null : (
            <Text size="sm" className="how-numeric">
              {formatPrice(chosen.priceCents, product.currency)}
            </Text>
          )}
        </Group>
        {product.description === null ? null : (
          <Text size="xs" c="dimmed">
            {product.description}
          </Text>
        )}
        {soldOut ? (
          <Badge variant="light" color="gray" w="fit-content">
            Sold out
          </Badge>
        ) : chosen !== null && chosen.available && typeof chosen.left === "number" ? (
          <Badge variant="light" color="orange" w="fit-content">
            {chosen.left} left
          </Badge>
        ) : null}
      </Stack>
      {soldOut ? null : (
        <Group gap="xs" wrap="nowrap" align="flex-end">
          {product.variants.length > 1 ? (
            <Select
              label="Option"
              aria-label={`Option for ${product.name}`}
              w={140}
              value={variantId}
              allowDeselect={false}
              data={product.variants.map((v) => ({
                value: v.id,
                label: v.available ? v.name : `${v.name} (sold out)`,
                disabled: !v.available,
              }))}
              onChange={(value) => {
                setVariantId(value);
                if (value !== null && quantity > 0) onPick(value, quantity);
              }}
            />
          ) : null}
          <NumberInput
            label="How many"
            aria-label={`How many ${product.name}`}
            w={100}
            min={0}
            max={max}
            allowDecimal={false}
            value={quantity}
            onChange={(value) => {
              if (chosen !== null)
                onPick(
                  chosen.id,
                  typeof value === "number" ? Math.max(0, Math.min(max, value)) : 0,
                );
            }}
          />
        </Group>
      )}
    </Group>
  );
}
